import { useCallback } from "react";
import type { FundMovement, Loan, Receivable, Settings } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import {
  removeLoan as apiRemoveLoan,
  removeReceivable as apiRemoveReceivable,
  saveLoan,
  saveReceivable,
  saveSettings,
} from "@/store/api";
import { uid } from "@/utils/format";

// Cartera de clientes y cuenta de préstamos Juank.unlimited.
export const useFinanceActions = (ctx: StoreCtx) => {
  const { data, setData, run } = ctx;

  const addReceivable = useCallback(
    (r: Receivable) =>
      run(async () => {
        const saved = await saveReceivable(r);
        setData((p) => ({ ...p, receivables: [saved, ...p.receivables] }));
      }),
    [run, setData]
  );

  const updateReceivable = useCallback(
    (r: Receivable) =>
      run(async () => {
        const saved = await saveReceivable(r);
        setData((p) => ({
          ...p,
          receivables: p.receivables.map((x) => (x.id === saved.id ? saved : x)),
        }));
      }),
    [run, setData]
  );

  const removeReceivable = useCallback(
    (id: string) =>
      run(async () => {
        await apiRemoveReceivable(id);
        setData((p) => ({ ...p, receivables: p.receivables.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  const addLoan = useCallback(
    (l: Loan) =>
      run(async () => {
        const saved = await saveLoan(l);
        // Un préstamo de Juank inyecta liquidez a la Caja Menor (y una devolución
        // la reduce), para que la cuenta de préstamos y los fondos queden cuadrados.
        const movement: FundMovement = {
          id: uid("fm"),
          date: saved.date || new Date().toISOString(),
          kind: saved.type === "Préstamo" ? "prestamo_juank" : "pago_prestamo",
          fromFund: saved.type === "Préstamo" ? null : "caja",
          toFund: saved.type === "Préstamo" ? "caja" : null,
          amount: saved.value,
          note: saved.concept,
        };
        const next: Settings = {
          ...data.settings,
          fundMovements: [...data.settings.fundMovements, movement],
        };
        await saveSettings(next);
        setData((p) => ({ ...p, loans: [saved, ...p.loans], settings: next }));
      }),
    [run, data.settings, setData]
  );

  const removeLoan = useCallback(
    (id: string) =>
      run(async () => {
        await apiRemoveLoan(id);
        setData((p) => ({ ...p, loans: p.loans.filter((x) => x.id !== id) }));
      }),
    [run, setData]
  );

  return { addReceivable, updateReceivable, removeReceivable, addLoan, removeLoan };
};